/**
 * Coarse land/ocean mask of the real Earth.
 *
 * Rasterised at author time from Natural Earth 110m physical land polygons
 * (public domain) into a 288 x 144 grid — 1.25 degrees per cell — then packed
 * one bit per cell and base64'd. 6.9 KB of source, no runtime fetch, no
 * geometry library, no image decode.
 *
 * Row 0 is 90N, column 0 is 180W. A set bit means land.
 *
 * At this resolution coastlines are quantised: a cell whose centre falls just
 * offshore reads as ocean even if a city sits on it. That is invisible on a
 * globe drawn a few hundred pixels wide, which is the only place this is used.
 */

export const MASK_COLS = 288;
export const MASK_ROWS = 144;

const PACKED =
    'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAO+AAAf/+AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAB' +
    '///x/+//9BAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAff/8f/////+AAAAAgAA+AAAAAPgAAAAAAAAAAAAAAAAAAAAAP7/Af' +
    '/////4AAAH8AAAAAAAAAHwAAAAAAAAAAAAAAAAAAAAFgH+f//////wAAAD5gAAAAAAAAAMAAAAAAAAAAAAAAAAAB4AA4/8H/////' +
    '/wAAAAwAAAAAAgAAAHwAAAAAAAAAAAAAAAAAPmPDjgAAf////wAAAAAAAAAD4AAA///gAAPwAAAAAAAAAAAGAAAAAAAAP////wAA' +
    'AAAAAAAOAAAH//8AAAAAAAAAAAAAAAAP2HHe7uAAD///+AAAAAAAAAAYAAD////+HgAAAAAAAAAAAAAff7Dg//AAD////AAAAAAA' +
    'AABwAdf//////gD/AAAAAAD8AAAAP/gcf/8AD///9AAAAAAfAAAAB8///////////wAAAAf//4//H/zvDh/ABf//8AAAAAH/4AAA' +
    '49////////////7+wB//////8HBvnBfgB//+AAAAAA///xh//+///////////////gP//////////Af8B//4AAAAAB///5v//9//' +
    '////////////fj//////////8AfMA/+AB/AAAD/P8P//////////////////BAH/////////3jfwA/4AAfAAAP+f8///////////' +
    '///////8AIv////////+AAHQAfwAAAAAA/5////////////////////+AB/////////4ADwAAPwAAAAAD/x/////////////////' +
    '/9/wAA/9n//////wAD8AABwAAAAAD/x/////////////////8D+AAAP5AP/////wAD8YAAAAAAAAD/4f///////////////+cOAA' +
    'AAA4AAf////8AD/8AAAAAAAIAHgP//////////////+AAcAAAABgAAP/////gB/+AAAAAAAcATh///////////////4AB8AAAAMA' +
    'AAP/////4D//AAAAAAAOA2B///////////////gAD8AAAAAAAAD//////P//wAAAAABnAYf///////////////wABwAAAAAAAAB/' +
    '/////H//4AAAAABvD//////////////////ABwAAAAAAAAA//////n//4AAAAADPn//////////////////gBgAAAAAAAABf////' +
    '////IAAAAAAOf/////////////////8gAAAAAAAAAAAX///////EeAAAAAAB//////////////////8wAAAAAAAAAAAL//////9w' +
    'fAAAAAAP//////////////////8AAAAAAAAAAAAH///////xBAAAAAAD/////f////////////4gAAAAAAAAAAAH////////AAAA' +
    'AAAB/9//M/w///////////wAAAAAAAAAAAAH///////YAAAAAAAB/8/+Afz///////////hgAAAAAAAAAAAP//////8AAAAAAAB/' +
    '4OP8APx//////////+DwAAAAAAAAAAAH//////8AAAAAAAB/wXn8MH4f/////////wCAAAAAAAAAAAAH//////gAAAAAAAB/gQ3l' +
    '//8/////////vgCAAAAAAAAAAAAH//////AAAAAAAAD/AQTn//8f///////+TADAAAAAAAAAAAAD//////AAAAAAAAB/AABn//4f' +
    '///////8DgGAAAAAAAAAAAAD/////+AAAAAAAAAcB4BD//+f////////xweAAAAAAAAAAAAB/////+AAAAAAAAAR/4AAB///////' +
    '////BxcAAAAAAAAAAAAA/////8AAAAAAAAAf/wAAB///////////AGwAAAAAAAAAAAAAP////4AAAAAAAAB//4AAD///////////' +
    'gOAAAAAAAAAAAAAAH////gAAAAAAAAD///DwD///////////gIAAAAAAAAAAAAAAE////gAAAAAAAAD///7/////////////gAAA' +
    'AAAAAAAAAAAAC//xhgAAAAAAAAD///////z/////////wAAAAAAAAAAAAAAABf/gAwAAAAAAAAP/////z/5/////////gAAAAAAA' +
    'AAAAAAAABP/AAwAAAAAAAAf/////5/8T////////AAAAAAAAAAAAAAAAAn/AAQAAAAAAAA//////8/8B////////AAAAAAAAAAAA' +
    'AAAAAD/AAEAAAAAAAA//////8/+YA//////+gAAAAAAAAAAAAAAAAB/AAgAAAAAAAB//////8f/+Af/////4gAAAAAAAAAAAAAAA' +
    'AA/ACMAAAAAAAD///////f//AP/+P//AAAAAAAAAAAAAAAAAAA/AwDAAAAAAAB///////P/+AD/8P/gAAAAAAAAAAAAQAAAAAA/h' +
    'wAMAAAAAAB///////H/8AD/wH/mAAAAAAAAAAAAAAAAAAAP/wGIgAAAAAB///////n/4AD/gH/gAwAAAAAAAAAAAAAAAAAD/gAAA' +
    'AAAAAB///////j/gAB/AD/wAwAAAAAAAAAAAAAAAAAAH+AAAAAAAAB///////z/AAB8AA/4AgAAAAAAAAAAAAAAAAAAD+AAAAAAA' +
    'AD///////74AAA8AA/4AAAAAAAAAAAAAAAAAAAAAOAAAAAAAAB///////9AAAA8AAX4AgAAAAAAAAAAAAAAAAAAAGAIAAAAAAB//' +
    '/////8CAAA8AAT4AAAAAAAAAAAAAAAAAAAAAGA+EAAAAAA///////++AAAcAARwBIAAAAAAAAAAAAAAAAAAABJ/+AAAAAAf/////' +
    '//8AAAaAAAgCKAAAAAAAAAAAAAAAAAAAAX//gAAAAAP///////8AAACAAQAAOAAAAAAAAAAAAAAAAAAAAD//gAAAAAH///////8A' +
    'AACAAIAEEAAAAAAAAAAAAAAAAAAAAD//+AAAAAD/D/////4AAAAAAMAGAAAAAAAAAAAAAAAAAAAAAD///gAAAAAABf////wAAAAA' +
    'BuAeAAAAAAAAAAAAAAAAAAAAAD///gAAAAAAAP////gAAAAAA2A8AAAAAAAAAAAAAAAAAAAAAH///wAAAAAAAP////AAAAAAAeB8' +
    'AAAAAAAAAAAAAAAAAAAAAP///wAAAAAAAP///+AAAAAAAcH88gAAAAAAAAAAAAAAAAAAAP///4AAAAAAAf///8AAAAAAAOH8AiAA' +
    'AAAAAAAAAAAAAAAAAf////AAAAAAAf///4AAAAAAAHD5gCIAAAAAAAAAAAAAAAAAAP////8AAAAAAP///wAAAAAAAHh5QzfAQAAA' +
    'AAAAAAAAAAAAAf/////AAAAAAH///wAAAAAAADgAQA/wQAAAAAAAAAAAAAAAAf/////wAAAAAD///gAAAAAAAAgAAAP5gAAAAAAA' +
    'AAAAAAAAAP/////wAAAAAD///wAAAAAAAAfAAAH8DAAAAAAAAAAAAAAAAH/////wAAAAAD///gAAAAAAAADwAAPmAQAAAAAAAAAA' +
    'AAAAAH/////wAAAAAD///wAAAAAAAAAAEAACAAAAAAAAAAAAAAAAAD/////gAAAAAB///wAAAAAAAAAAAAAAAEAAAAAAAAAAAAAA' +
    'AD/////AAAAAAB///wAAAAAAAAAAACAAAAAAAAAAAAAAAAAAAB////+AAAAAAD///wEAAAAAAAAAAPhgAAAAAAAAAAAAAAAAAB//' +
    '//+AAAAAAD///4MAAAAAAAAABPhwAAAAAAAAAAAAAAAAAA////+AAAAAAH///wcAAAAAAAAAH/hwAAQAAAAAAAAAAAAAAAP///+A' +
    'AAAAAH///h8AAAAAAAAAP/54AAAAAAAAAAAAAAAAAAH///8AAAAAAH//+B8AAAAAAAAAP//4AAACAAAAAAAAAAAAAAD///8AAAAA' +
    'AD//8A4AAAAAAAAAf//8AAAAAAAAAAAAAAAAAAD///8AAAAAAB//8B4AAAAAAAAD///+ABAAAAAAAAAAAAAAAAD///4AAAAAAB//' +
    '8BwAAAAAAAAP////AAAAAAAAAAAAAAAAAAD///gAAAAAAA//8BwAAAAAAAAf////gAAAAAAAAAAAAAAAAAD//8AAAAAAAA//8BwA' +
    'AAAAAAAf////wAAAAAAAAAAAAAAAAAH//4AAAAAAAA//wAAAAAAAAAAf////wAAAAAAAAAAAAAAAAAH//4AAAAAAAA//wAAAAAAA' +
    'AAAf////4AAAAAAAAAAAAAAAAAH//4AAAAAAAAf/wAAAAAAAAAAf////4AAAAAAAAAAAAAAAAAH//wAAAAAAAAf/gAAAAAAAAAAP' +
    '////4AAAAAAAAAAAAAAAAAH//wAAAAAAAAP/AAAAAAAAAAAP////wAAAAAAAAAAAAAAAAAH//gAAAAAAAAH+AAAAAAAAAAAH/j//' +
    'wAAAAAAAAAAAAAAAAAH//AAAAAAAAAH8AAAAAAAAAAAH4A//gAAAAAAAAAAAAAAAAAP/uAAAAAAAAAGAAAAAAAAAAAAPAAv/gAAA' +
    'AAAAAAAAAAAAAAP/wAAAAAAAAAAAAAAAAAAAAAAAAAH/AAAQAAAAAAAAAAAAAAP/4AAAAAAAAAAAAAAAAAAAAAAAAAD/AAAIAAAA' +
    'AAAAAAAAAAf/wAAAAAAAAAAAAAAAAAAAAAAAAAB8AAAOAAAAAAAAAAAAAAf8AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAcAAAAAAAA' +
    'AAAAAAf8AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIAAAAAAAAAAAAAAfwAAAAAAAAAAAAAAAAAAAAAAAAAAAOAABgAAAAAAAAAAAA' +
    'AAPwAAAAAAAAAAAAAAAAAAAAAAAAAAAEAADAAAAAAAAAAAAAAA/wAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAOAAAAAAAAAAAAAAA/A' +
    'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAeAAAAAAAAAAAAAAA/gAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/gAAAA' +
    'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/AAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAA+AAAAAAAAA' +
    'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA+BwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAaAAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAPgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
    'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAMAAAAAAAAAAAAAAAAAAAAAAAAAAgAAAAA' +
    'AAAAAAAAAAAAAAA4AAAAAAAAAAAAAAB8AAACAPn8f/zwAAAAAAAAAAAAAAAAAAAwAAAAAAAAAAAAAA///gD////////8AAAAAAAA' +
    'AAAAAAAAAAF8AAAAAAAAAAAAPP///wP/////////+AAAAAAAAAAAAAAAAAG8AAAAAAAADz///////B///////////8AAAAAAAAAA' +
    'AAMAAA++AAAAAAH//////////H////////////+AAAAAAAAAAAeAwAD+AAAAAA////////////////////////8AAAAAAApAfAH/' +
    '///+AAAAAA////////////////////////gAAAAAH//////////gAAAAAP///////////////////////+AAAAAAf////////8AA' +
    'AAAAf////////////////////////+AAAAA///////////AAAHAP//////////////////////////AAAAOA/////////8AAAfgc' +
    '/////////////////////////4AAAAAAP/////////4PB+AA/////////////////////////wAAAAAH///////////8AfP/////' +
    '/////////////////////8AAAAAH////////////v/////////////////////////////wA98AAf///////////////////////' +
    '///////////////////8////////////////////////////////////////////////////////////////////////////////' +
    '////////////////////////////////////////////////////////////////////////////////////////////////////' +
    '////////////';

let bits = null;

/** Decodes base64 to bytes once, on first use. */
function ensureDecoded() {
    if (bits) return bits;
    const bin = atob(PACKED);
    bits = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bits[i] = bin.charCodeAt(i);
    return bits;
}

/**
 * @param {number} lat degrees, +90 north
 * @param {number} lon degrees, +180 east
 * @returns {boolean} true if that cell is land
 */
export function isLand(lat, lon) {
    const b = ensureDecoded();
    let r = Math.floor((90 - lat) / (180 / MASK_ROWS));
    let c = Math.floor((lon + 180) / (360 / MASK_COLS));
    if (r < 0) r = 0; else if (r >= MASK_ROWS) r = MASK_ROWS - 1;
    if (c < 0) c = 0; else if (c >= MASK_COLS) c = MASK_COLS - 1;
    const i = r * MASK_COLS + c;
    return (b[i >> 3] & (1 << (7 - (i & 7)))) !== 0;
}
